import * as React from "react";
import { Box, Stack, Checkbox, FormControlLabel, Alert } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { AppVersion } from "../../types/appVersion";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetAppVersionByIdQuery,
  useAddAppVersionMutation,
  useEditAppVersionMutation,
} from "../../services/app-version.service";
import { BaseModal, StyledTextField, Button, SelectBase, SelectOption } from "../../components/global";

const platformOptions: SelectOption[] = [
  { value: "ios", label: "ios" },
  { value: "android", label: "android" },
];

interface ModalAppUpdateFormProps {
  open: boolean;
  onClose: () => void;
  data: AppVersion | null;
}

const ModalAppUpdateForm = ({
  open,
  onClose,
  data,
}: ModalAppUpdateFormProps) => {
  const { data: appVersion } = useGetAppVersionByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useAddAppVersionMutation();
  const [edit, { isLoading: editing }] = useEditAppVersionMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("app_id", data?.app_id || "");
      setValue("platform", data?.platform || platformOptions[0].value);
      setValue("version", data?.version || 1);
      setValue("version_name", data?.version_name || "");
      setValue("mandatory", data?.mandatory || false);
      setValue("release_notes", data?.release_notes || "");
      setValue("enabled", data?.enabled || false);
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (appVersion) {
      setValue("app_id", appVersion?.app_id || "");
      setValue("platform", appVersion?.platform || platformOptions[0].value);
      setValue("version", appVersion?.version || 1);
      setValue("version_name", appVersion?.version_name || "");
      setValue("mandatory", appVersion?.mandatory || false);
      setValue("release_notes", appVersion?.release_notes || "");
      setValue("enabled", appVersion?.enabled || false);
    }
  }, [appVersion, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} App Version`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="app_id"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="App ID"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.app_id}
                  helperText={!!errors.app_id && "This field is required"}
                />
              )}
            />
            <Controller
              name="platform"
              control={control}
              render={({ field: { onChange, value } }) => {
                const selectedOption = platformOptions.find(
                  (opt) => opt.value === value
                ) || platformOptions[0];
                return (
                  <SelectBase
                    options={platformOptions}
                    value={selectedOption}
                    onChange={(newValue) => {
                      if (newValue && !Array.isArray(newValue)) {
                        onChange(newValue.value);
                      } else {
                        onChange(platformOptions[0].value);
                      }
                    }}
                    label="Platform"
                    placeholder="Select platform..."
                  />
                );
              }}
            />
            <Controller
              name="version"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Version"
                  value={value || ""}
                  fullWidth
                  type="number"
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.version}
                  helperText={!!errors.version && "This field is required"}
                />
              )}
            />
            <Controller
              name="version_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Version Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.version_name}
                  helperText={!!errors.version_name && "This field is required"}
                />
              )}
            />
            <Controller
              name="mandatory"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value || false} />}
                  label="Mandatory"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                    },
                  }}
                />
              )}
            />
            <Controller
              name="release_notes"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Release Notes"
                  value={value || ""}
                  fullWidth
                  multiline
                  rows={5}
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  placeholder="Enter release notes (optional)"
                />
              )}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              Only enable if above version is already LIVE on Google/Apple app market
            </Alert>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value || false} />}
                  label="Enabled"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                    },
                  }}
                />
              )}
            />
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalAppUpdateForm;
